import { types } from "mobx-state-tree";
import ReasonCode from "../ReasonCodes/ReasonCode";
import StatusCode from "../StatusCodes/StatusCode";
import Location from "../Locations/Location";
import AgentTeam from "./AgentTeam";
import Team from "../Teams/Team";

const Agent = types
  .model("Agent", {
    id: types.identifier,
    fullName: types.optional(types.string, ""),
    phoneNumber: types.optional(types.string, ""),
    locationCode: types.maybeNull(types.reference(types.late(() => Location))),
    statusChangeTime: types.maybeNull(types.string),
    registered: types.optional(types.boolean, false),
    reasonCode: types.maybeNull(types.reference(types.late(() => ReasonCode))),
    teams: types.maybeNull(types.array(AgentTeam)),
    statusCode: types.maybeNull(types.reference(types.late(() => StatusCode))),

    queueDescription: types.maybeNull(types.optional(types.string, "")),
    applicationDescription: types.maybeNull(types.optional(types.string, "")),
    applicationInstanceDescription: types.maybeNull(types.optional(types.string, "")),
    callReference: types.maybeNull(types.optional(types.string, "")),
    currentCallTeam: types.maybeNull(types.reference(types.late(() => Team))),
    currentCallCli: types.maybeNull(types.optional(types.string, "")),
    alertTime: types.maybeNull(types.string),
  })
  .views((self) => ({
    getIsInOutboundCall() {
      // Status 2 = outbound
      return self.statusCode &&
        (self.statusCode.code === 5);
    },
    getIsInCall() {
      // Status 2 = inbound, 5 = outbound
      return self.statusCode &&
        (self.statusCode.code === 2 || self.statusCode.code === 5);
    },
    get loggedInTeams() {
      return self.teams.filter((team) => team.loggedIn);
    },
  }))
  .actions((self) => ({
    setFullName(fullName) {
      self.fullName = fullName;
    },
    setRegistered(registered) {
      self.registered = registered;
    },
    setStatusCode(statusCode) {
      self.statusCode = statusCode || null;
    },
    setReasonCode(reasonCode) {
      self.reasonCode = reasonCode || null;
    },
    setLocationCode(locationCode) {
      self.locationCode = locationCode;
    },
    setTeams(teams) {
      let newTeams = [];
      for (let team of teams) {
        const existingTeam = self.teams.find((t) => t.team.id === team.id);
        if (existingTeam) {
          // Don't create a new AgentTeam - we already have one. Just update relevant data within it.
          existingTeam.loggedIn = team.loggedIn;
          newTeams.push(existingTeam);
        } else {
          // This team doesn't exist already, so create it
          newTeams.push(
            AgentTeam.create({ team: team.id, loggedIn: team.loggedIn })
          );
        }
      }
      self.teams = [...newTeams];
    },
    setStatusChangeTime(time) {
      self.statusChangeTime = time;
    },
    updateTeamStatus(id, status) {
      const team = self.teams.find((t) => t.team.id === id);
      if (team) {
        team.setStatus(status);
      }
    },
    removeTeams(ids) {
      ids.forEach((id) => {
        let a = self.teams.find((t) => t.team.id === id);
        if(a) {
          self.teams.remove(a);
        }
      });
    },
    addTeams(teamslist) {
      teamslist.forEach((t) => {
        let newTeam = AgentTeam.create({ team: t.id, loggedIn: t.loggedIn });
        self.teams.push(newTeam)
      });
    },
    update: (data) => {
      self.fullName = data.fullName;
      self.registered = data.registered;
      self.statusCode = data.statusCode;
      self.reasonCode = data.reasonCode;
      self.locationCode = data.locationCode;
      self.statusChangeTime = data.statusChangeTime;
      self.setTeams(data.teams);
    },
    setCallDetails: (data) => {
      // Call details
      self.queueDescription = data.queueDescription;
      self.applicationDescription = data.applicationDescription;
      self.applicationInstanceDescription = data.applicationInstanceDescription;
      self.callReference = data.callReference;
      self.currentCallTeam = data.currentCallTeam ? data.currentCallTeam.id : null;
      self.currentCallCli = data.currentCallCli;
    },
    setAlertTime: (alertTime) => {
      self.alertTime = alertTime;
    },
    setRegistrationDetails(regDetails) {
      self.registered = regDetails.registered;
      self.locationCode = regDetails.locationCode;
      self.phoneNumber = regDetails.phoneNumber;
    },
  }))
  .views((self) => ({
    getStatusDisplayText() {
      let displayText = "";

      if (self.statusCode) {
        if (self.statusCode.code === 4 && self.reasonCode !== null) {
          displayText = self.statusCode.description + " (" + self.reasonCode.description + ")";
        } else {
          displayText = self.statusCode.description;
        }
      }
      return displayText;
    },
  }));

export default Agent;
